'use client';

import React, { useState } from 'react';
import styled from 'styled-components';
import { Checkbox } from '@/components/forms/controls/Checkbox';
import { NotificationDelayModal } from '@/components/modals';
import { Input } from '@/components/forms/controls/Input';

interface NotificationSettings {
  delay: string;
  repeat: string;
}

interface NotificationMethod {
  email: {
    enabled: boolean;
    address: string;
    settings: NotificationSettings;
  };
}

interface NotificationMethodsSectionProps {
  methods: NotificationMethod;
  onMethodsChange: (methods: NotificationMethod) => void;
  monitorName?: string;
  className?: string;
}

const Container = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 24px 0;
`;

const MethodsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;

  @media (max-width: 1280px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MethodCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MethodInfo = styled.div`
  font-size: 14px;
  color: #6b7280;
  margin-left: 4px;

  a {
    color: #5B47E5;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const EmailInputWrapper = styled.div`
  margin-left: 4px;
  margin-top: 8px;
  width: 100%;
`;

/* const AddLink = styled.button`
  font-size: 14px;
  color: #FB6F2A;
  text-decoration: underline;
  cursor: pointer;
  margin-left: 4px;
  background: transparent;
  border: none;
  padding: 0;
  text-align: left;

  &:hover {
    color: #D33324;
  }
`; */

const DelayInfo = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #9ca3af;
  background: transparent;
  border: none;
  padding: 0;
  margin-left: 4px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #6b7280;
    
    svg {
      transform: rotate(180deg);
    }
  }

  svg {
    width: 14px;
    height: 14px;
    transition: transform 0.3s ease;
  }
`;

/* const PhoneInputWrapper = styled.div`
  margin-left: 4px;
  margin-top: 8px;
`; */

// Email validation function
const isValidEmail = (email: string): boolean => {
  if (!email || email?.trim() === '') return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email?.trim());
};

export const NotificationMethodsSection: React.FC<NotificationMethodsSectionProps> = ({
  methods,
  onMethodsChange,
  monitorName,
  className,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [currentNotificationType, setCurrentNotificationType] = useState<'email'>('email');
  const [tempDelay, setTempDelay] = useState('none');
  const [tempRepeat, setTempRepeat] = useState('none');
  const [emailError, setEmailError] = useState<string | null>(null);

  const handleOpenModal = (type: 'email') => {
    setCurrentNotificationType(type);
    const settings = methods[type].settings;
    setTempDelay(settings.delay);
    setTempRepeat(settings.repeat);
    setModalOpen(true);
  };

  const handleSaveSettings = () => {
    const updatedMethods = {
      ...methods,
      [currentNotificationType]: {
        ...methods[currentNotificationType],
        settings: { delay: tempDelay, repeat: tempRepeat },
      },
    };
    onMethodsChange(updatedMethods);
    setModalOpen(false);
  };

  // SMS, Voice, Push removed

  const getDelayText = (settings: NotificationSettings) => {
    const hasDelay = settings.delay !== 'none';
    const hasRepeat = settings.repeat !== 'none';
    
    if (!hasDelay && !hasRepeat) {
      return 'No delay, no repeat';
    }
    
    const parts = [];
    if (hasDelay) {
      const delayLabel = settings.delay.replace('m', ' min').replace('h', ' hour');
      parts.push(`Delay: ${delayLabel}`);
    }
    if (hasRepeat) {
      const repeatLabel = settings.repeat.replace('m', ' min').replace('h', ' hour');
      parts.push(`Repeat: ${repeatLabel}`);
    }
    return parts.join(', ');
  };

  const getNotificationTypeLabel = () => 'E-mail';

  return (
    <Container className={className}>
      <SectionTitle>How will we notify you?</SectionTitle>
      
      <MethodsGrid>
        {/* Email only */}
        <MethodCard>
          <Checkbox
            checked={methods.email.enabled}
            onChange={(checked) => {
              onMethodsChange({
                ...methods,
                email: { 
                  ...methods.email, 
                  enabled: checked,
                  // Clear email address when unchecked
                  address: checked ? methods.email.address : '',
                },
              });
              // Clear error when checkbox is toggled
              setEmailError(null);
            }}
            label="E-mail"
          />
          {methods.email.enabled && (
            <EmailInputWrapper>
              <Input
                type="email"
                value={methods.email.address}
                onChange={(e) => {
                  onMethodsChange({
                    ...methods,
                    email: { ...methods.email, address: e.target.value },
                  });
                  // Clear error on change
                  if (emailError) {
                    setEmailError(null);
                  }
                }}
                onBlur={(e) => {
                  const emailValue = e.target.value?.trim();
                  if (!emailValue) {
                    setEmailError('Email address is required');
                  } else if (!isValidEmail(emailValue)) {
                    setEmailError('Please enter a valid email address');
                  } else {
                    setEmailError(null);
                  }
                }}
                onFocus={() => {
                  setEmailError(null);
                }}
                placeholder="Enter email address"
                fullWidth
                required
                error={!!emailError}
                helperText={emailError || undefined}
              />
            </EmailInputWrapper>
          )}
          {/* <DelayInfo onClick={() => handleOpenModal('email')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {getDelayText(methods.email.settings)}
          </DelayInfo> */}
        </MethodCard>
      </MethodsGrid>

      <NotificationDelayModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        notificationType={getNotificationTypeLabel()}
        monitorName={monitorName}
        repeatValue={tempRepeat}
        delayValue={tempDelay}
        onRepeatChange={setTempRepeat}
        onDelayChange={setTempDelay}
        onSave={handleSaveSettings}
      />
    </Container>
  );
};

export default NotificationMethodsSection;

